import React, { useEffect, useState } from 'react';
import { useCheckAuth } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { initiateSignIn } from '@src/apis/reware';
import getOktaAuth from '@src/services/oktaService';
import { AuthLayout } from './AuthLayout';

/*
 * Email-first /login. The user enters one email; the backend (`initiateSignIn`) decides how the
 * account signs in and, for passwordless, sends the email as a side effect. We branch on the result:
 *   - SSO  → hand off to Okta (a full redirect), using the resolved idp when there is one.
 *   - MAGIC_LINK → go to the code page (also the enumeration-safe path for unknown emails).
 * Badge sign-in lives on /badge-login. Styled via the shared <AuthLayout>; the
 * redirect-if-already-authenticated effect mirrors what react-admin's default <Login> did for us.
 */
const CustomLogin = () => {
  const checkAuth = useCheckAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth({}, false)
      .then(() => navigate('/'))
      .catch(() => {
        /* not authenticated — stay on the login page */
      });
  }, [checkAuth, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      const result = await initiateSignIn({ email: trimmedEmail });
      if (result.method === 'SSO') {
        // Registered account whose partner requires SSO — hand off to the IdP (a full redirect).
        // `idpId` is only present on the SSO branch of the discriminated union.
        await getOktaAuth().signInWithRedirect(result.idpId ? { idp: result.idpId } : {});
        return;
      }
      // Passwordless: the email is on its way if the account exists. This is also what an unknown
      // email gets, so the destination never reveals whether the address is registered.
      void navigate(`/magic-link-confirm?email=${encodeURIComponent(trimmedEmail)}`);
    } catch (err) {
      console.error('Sign-in initiation failed:', err);
      setError('Something went wrong starting sign-in. Please try again.');
      setLoading(false);
    }
  };

  return (
    <AuthLayout heading="Sign in to Trove" subcopy="Enter your email to continue.">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={loading}
          autoFocus
          autoComplete="email"
          sx={{ mb: 1.5 }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ height: 44, fontSize: 15 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Continue'}
        </Button>
      </Box>

      <Divider sx={{ my: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
          or
        </Typography>
      </Divider>

      <Button fullWidth variant="text" sx={{ height: 44 }} onClick={() => navigate('/badge-login')}>
        Sign in with a badge
      </Button>
    </AuthLayout>
  );
};

export default CustomLogin;
